/**
 * A previsão de UM ponto (a ficha do "Próximos dias") — funções puras,
 * testadas em `ponto.test.ts`.
 *
 * - **O número da ficha é o do mapa**: a mesma bilinear com máscara de
 *   `interp.ts`, sobre os mesmos quatro pontos da grade, da mesma rodada. Os
 *   pontos vêm dos blocos de 2°×2° da série (`serie/<bi>_<bj>.bin`); perto da
 *   borda de um bloco, os vizinhos estão no bloco ao lado (até 4 blocos).
 * - **Uma grade de tempo só**, de 3 em 3 h: o IFS tem passos de 3 h até
 *   +144 h e de 6 h depois; o CAMS, de 3 h até +120 h. Nos passos de 6 h, os
 *   valores de instante (temperatura, umidade, vento, MP2,5) são
 *   interpolados no tempo, e a chuva (uma taxa em mm/h) vale o intervalo
 *   inteiro. Assim os dois modelos dividem o mesmo eixo e o mesmo cursor.
 * - **Os dias são os do lugar** (três fusos na Amazônia Legal: AC −5;
 *   AM, RO, RR, MT −4; PA, AP, TO, MA −3), não os de Brasília.
 */
import { HOUR_MS } from '../lib/time';
import type { GradeModelo, Modelo } from './api';
import { bilinear } from './interp';

export const PASSO_GRADE_MS = 3 * HOUR_MS;

// ---------------------------------------------------------------------------
// Pontos e blocos

/** Linha e coluna fracionárias do lon/lat na grade, ou null fora dela. */
export function posicao(g: GradeModelo, lon: number, lat: number): [number, number] | null {
  const fi = (g.lat0 - lat) / g.passo;
  const fj = (lon - g.lon0) / g.passo;
  if (!(fi >= -0.5 && fj >= -0.5 && fi <= g.ni - 0.5 && fj <= g.nj - 0.5)) return null;
  return [fi, fj];
}

/** Os (até) quatro pontos da grade que a bilinear usa, e o peso fracionário entre eles. */
export function vizinhos(g: GradeModelo, fi: number, fj: number): { linhas: [number, number]; colunas: [number, number]; ti: number; tj: number } {
  const i = Math.min(Math.max(fi, 0), g.ni - 1);
  const j = Math.min(Math.max(fj, 0), g.nj - 1);
  const i0 = Math.min(Math.floor(i), Math.max(g.ni - 2, 0));
  const j0 = Math.min(Math.floor(j), Math.max(g.nj - 2, 0));
  return { linhas: [i0, Math.min(i0 + 1, g.ni - 1)], colunas: [j0, Math.min(j0 + 1, g.nj - 1)], ti: i - i0, tj: j - j0 };
}

export const chaveBloco = (bi: number, bj: number) => `${bi}_${bj}`;

/** Blocos necessários para o ponto (1 a 4). */
export function blocosDoPonto(g: GradeModelo, fi: number, fj: number): [number, number][] {
  const v = vizinhos(g, fi, fj);
  const out = new Map<string, [number, number]>();
  for (const i of v.linhas) for (const j of v.colunas) out.set(chaveBloco(Math.floor(i / g.bloco), Math.floor(j / g.bloco)), [Math.floor(i / g.bloco), Math.floor(j / g.bloco)]);
  return [...out.values()];
}

/**
 * Série no ponto, passo a passo: a bilinear com máscara (a de `interp.ts`)
 * sobre os quatro pontos da grade. Com várias variáveis, `combinar` junta os
 * valores EM CADA PONTO antes da bilinear — o vento é a velocidade de u e v
 * em cada ponto, interpolada depois, exatamente como o mapa faz
 * (`camadas.ts`); interpolar u e v primeiro daria um vento menor que o do mapa.
 * Blocos em `[var][passo][linha][coluna]`.
 */
export function serieNoPontoDe(
  m: Pick<Modelo, 'grade' | 'passos' | 'ordemVars' | 'vars'>,
  blocos: ReadonlyMap<string, Uint16Array>,
  variaveis: readonly string[],
  combinar: (valores: number[]) => number,
  fi: number,
  fj: number,
  semDado = 65535,
): Float32Array {
  const g = m.grade;
  const b = g.bloco;
  const ns = m.passos.length;
  const out = new Float32Array(ns).fill(NaN);
  const ivs = variaveis.map((v) => m.ordemVars.indexOf(v));
  const defs = variaveis.map((v) => m.vars[v]);
  if (ivs.some((iv) => iv < 0) || defs.some((d) => !d)) return out;
  const v = vizinhos(g, fi, fj);
  const ler = (iv: number, def: { off: number; esc: number }, i: number, j: number, k: number): number => {
    const bloco = blocos.get(chaveBloco(Math.floor(i / b), Math.floor(j / b)));
    if (!bloco) return NaN;
    const q = bloco[((iv * ns + k) * b + (i % b)) * b + (j % b)];
    return q === semDado || q === undefined ? NaN : def.off + def.esc * q;
  };
  const noPonto = (i: number, j: number, k: number) => combinar(ivs.map((iv, n) => ler(iv, defs[n]!, i, j, k)));
  const mini = { ni: 2, nj: 2 };
  const [i0, i1] = v.linhas;
  const [j0, j1] = v.colunas;
  for (let k = 0; k < ns; k++) {
    out[k] = bilinear([noPonto(i0, j0, k), noPonto(i0, j1, k), noPonto(i1, j0, k), noPonto(i1, j1, k)], mini, v.ti, v.tj);
  }
  return out;
}

/** Série de UMA variável no ponto (ver `serieNoPontoDe`). */
export function serieNoPonto(
  m: Pick<Modelo, 'grade' | 'passos' | 'ordemVars' | 'vars'>,
  blocos: ReadonlyMap<string, Uint16Array>,
  variavel: string,
  fi: number,
  fj: number,
  semDado = 65535,
): Float32Array {
  return serieNoPontoDe(m, blocos, [variavel], ([x]) => x!, fi, fj, semDado);
}

// ---------------------------------------------------------------------------
// Grade de tempo de 3 h

/** Valor de instante num tempo qualquer: interpolação linear entre os passos vizinhos; NaN fora deles. */
export function noInstante(instantes: readonly number[], valores: ArrayLike<number>, t: number): number {
  for (let k = 0; k < instantes.length; k++) {
    if (instantes[k] === t) return valores[k];
    if (instantes[k] > t) {
      if (k === 0) return NaN;
      const a = instantes[k - 1];
      const f = (t - a) / (instantes[k] - a);
      return valores[k - 1] + (valores[k] - valores[k - 1]) * f;
    }
  }
  return NaN;
}

/** Taxa (por hora) do intervalo que contém o instante: o valor do intervalo dividido pela duração dele. */
export function taxaNoIntervalo(instantes: readonly number[], valores: ArrayLike<number>, t: number): number {
  for (let k = 1; k < instantes.length; k++) {
    if (t > instantes[k - 1] && t <= instantes[k]) return valores[k] / ((instantes[k] - instantes[k - 1]) / HOUR_MS);
  }
  return NaN;
}

/** Valor do intervalo (passo anterior, passo] que contém o instante (a rajada máxima do intervalo, por exemplo). */
export function valorDoIntervalo(instantes: readonly number[], valores: ArrayLike<number>, t: number): number {
  for (let k = 1; k < instantes.length; k++) if (t > instantes[k - 1] && t <= instantes[k]) return valores[k];
  return NaN;
}

/** Instantes de 3 em 3 h de `inicio` (arredondado para baixo) até `fim`. */
export function gradeDeTempo(inicio: number, fim: number): number[] {
  const out: number[] = [];
  for (let t = Math.floor(inicio / PASSO_GRADE_MS) * PASSO_GRADE_MS; t <= fim; t += PASSO_GRADE_MS) out.push(t);
  return out;
}

// ---------------------------------------------------------------------------
// Fuso do lugar

/** Fuso (horas em relação ao UTC) pela UF. */
const FUSO_DA_UF: Record<string, number> = { AC: -5, AM: -4, RO: -4, RR: -4, MT: -4, PA: -3, AP: -3, TO: -3, MA: -3 };

/**
 * O fuso do lugar: pela UF do município; fora da malha (rio, fronteira),
 * pela longitude. Limitação conhecida: 13 municípios do sudoeste do
 * Amazonas seguem o fuso do Acre (−5) e aqui ficam em −4.
 */
export function fusoDoLugar(uf: string | null, lon: number): number {
  if (uf && uf in FUSO_DA_UF) return FUSO_DA_UF[uf];
  return lon >= -52.5 ? -3 : lon >= -67.5 ? -4 : -5;
}

/** Nome da cidade de referência do fuso, para o aviso "hora de Rio Branco (−2 h de Brasília)". */
export function nomeDoFuso(fuso: number): string {
  return fuso === -5 ? 'Rio Branco' : fuso === -4 ? 'Manaus' : 'Brasília';
}

const DIAS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];
const DIAS_LONGOS = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];

/** Meia-noite (epoch ms, UTC) do dia local que contém o instante. */
export function inicioDoDiaLocal(t: number, fuso: number): number {
  const off = fuso * HOUR_MS;
  return Math.floor((t + off) / (24 * HOUR_MS)) * 24 * HOUR_MS - off;
}

export function horaLocal(t: number, fuso: number): number {
  return new Date(t + fuso * HOUR_MS).getUTCHours();
}

export function rotuloDia(inicio: number, fuso: number, longo = false): string {
  const d = new Date(inicio + fuso * HOUR_MS);
  return `${(longo ? DIAS_LONGOS : DIAS)[d.getUTCDay()]} ${d.getUTCDate()}`;
}

// ---------------------------------------------------------------------------
// Conversões da ficha

/**
 * Índice de calor da NOAA (Rothfusz, com os ajustes), em °C — só vale com
 * calor: abaixo de 27 °C devolve NaN e a ficha mostra só a temperatura.
 */
export function sensacaoTermica(tC: number, ur: number): number {
  if (!(tC >= 27) || !Number.isFinite(ur)) return NaN;
  const T = tC * 1.8 + 32;
  const R = Math.min(100, Math.max(0, ur));
  let hi = 0.5 * (T + 61 + (T - 68) * 1.2 + R * 0.094);
  if (hi >= 80) {
    hi = -42.379 + 2.04901523 * T + 10.14333127 * R - 0.22475541 * T * R - 0.00683783 * T * T - 0.05481717 * R * R + 0.00122874 * T * T * R + 0.00085282 * T * R * R - 0.00000199 * T * T * R * R;
    if (R < 13 && T >= 80 && T <= 112) hi -= ((13 - R) / 4) * Math.sqrt((17 - Math.abs(T - 95)) / 17);
    else if (R > 85 && T >= 80 && T <= 87) hi += ((R - 85) / 10) * ((87 - T) / 5);
  }
  return (hi - 32) / 1.8;
}

export interface CategoriaAr {
  nome: string;
  cor: string;
  /** Efeitos à saúde — resumo do Guia Técnico do IQAr (MMA) e da CETESB, as mesmas faixas. */
  saude: string;
}

/** Faixas da Resolução CONAMA nº 491/2018 para o MP2,5 em 24 h (µg/m³), cores do MonitorAr. */
export function categoriaConama(mp25: number): CategoriaAr | null {
  if (!Number.isFinite(mp25)) return null;
  if (mp25 <= 25) return { nome: 'Boa', cor: '#88CE40', saude: 'Sem efeitos esperados à saúde.' };
  if (mp25 <= 50)
    return { nome: 'Moderada', cor: '#FFD156', saude: 'Grupos sensíveis (crianças, idosos, quem tem doença respiratória ou cardíaca) podem ter tosse seca e cansaço.' };
  if (mp25 <= 75) return { nome: 'Ruim', cor: '#FFAA71', saude: 'Toda a população pode ter tosse seca, cansaço e ardor nos olhos, nariz e garganta; grupos sensíveis, efeitos mais sérios.' };
  if (mp25 <= 125) return { nome: 'Muito ruim', cor: '#FF0000', saude: 'Os sintomas pioram em toda a população, com falta de ar e respiração ofegante; efeitos ainda mais graves nos grupos sensíveis.' };
  return { nome: 'Péssima', cor: '#9B1BAF', saude: 'Riscos sérios de doenças respiratórias e cardiovasculares para toda a população.' };
}

export type ParteDoDia = 'madrugada' | 'manhã' | 'tarde' | 'noite' | 'o dia todo';

export function parteDoDia(hora: number): ParteDoDia {
  return hora < 6 ? 'madrugada' : hora < 12 ? 'manhã' : hora < 18 ? 'tarde' : 'noite';
}

/** A parte do dia com mais chuva; "o dia todo" quando três ou mais partes têm, cada uma, ≥ 20% da chuva do dia. */
function quando(porParte: Map<ParteDoDia, number>, total: number): ParteDoDia {
  const partes = [...porParte].sort((a, b) => b[1] - a[1]);
  if (partes.filter(([, mm]) => mm >= 0.2 * total).length >= 3) return 'o dia todo';
  return partes[0]![0];
}

export interface ResumoDoDia {
  inicio: number;
  rotulo: string;
  rotuloLongo: string;
  /** Hoje (o dia do lugar em que estamos). */
  hoje: boolean;
  /** O dia não está inteiro na previsão (hoje, ou o último da rodada). */
  parcial: boolean;
  tendencia: boolean;
  tMax: number;
  tMin: number;
  /** Chuva do dia (mm), somando as taxas da grade de 3 h. */
  chuva: number;
  /** A parte do dia com mais chuva, quando chove (≥ 1 mm no dia); "o dia todo" quando ela se espalha. */
  quandoChove: ParteDoDia | null;
  rajadaMax: number;
  /** Média de 24 h do MP2,5, só com o dia (quase) inteiro coberto pelo CAMS. */
  mp25Media: number;
  ar: CategoriaAr | null;
  /** % dos cenários do WeatherNext 2 com ≥ 1 mm no dia (`chanceDoDia`); NaN sem o acesso de pesquisa ou sem a janela. */
  chanceDia: number;
}

export interface SeriesDoPonto {
  /** A grade de 3 h. */
  t: number[];
  chuva: Float32Array;
  t2m: Float32Array;
  ur: Float32Array;
  vento: Float32Array;
  /** Para onde o vento vai (graus a partir do norte), só nos passos do modelo; NaN entre eles. */
  rumo: Float32Array;
  rajada: Float32Array;
  pm25: Float32Array;
  /**
   * Com o acesso de pesquisa (WeatherNext 2), na mesma grade de 3 h: a chance
   * de chuva (% dos 64 cenários com ≥ 1 mm no intervalo de 6 h que contém a
   * fatia) e a temperatura abaixo da qual ficam 10% e 90% dos cenários.
   */
  chance?: Float32Array;
  t10?: Float32Array;
  t90?: Float32Array;
}

/** A chance de 24 h do WeatherNext 2 nos passos dele (6 h): a de cada janela de 24 h que termina no passo. */
export interface ChanceDe24h {
  instantes: readonly number[];
  chance24: ArrayLike<number>;
}

/**
 * A chance de chuva (≥ 1 mm) no dia local: a janela de 24 h do WeatherNext 2
 * que termina no passo mais perto do fim do dia (os passos são de 00, 06, 12 e
 * 18 UTC; o fim do dia é 03, 04 ou 05 UTC: até 3 h de diferença). NaN sem
 * janela inteira (os primeiros passos da rodada).
 */
export function chanceDoDia(c: ChanceDe24h | null | undefined, inicio: number): number {
  if (!c) return NaN;
  const fim = inicio + 24 * HOUR_MS;
  let melhor = -1;
  for (let k = 0; k < c.instantes.length; k++) {
    const d = Math.abs(c.instantes[k] - fim);
    // Empate (o fim do dia a 3 h de dois passos): o mais tarde.
    if (d <= 3 * HOUR_MS && (melhor < 0 || d <= Math.abs(c.instantes[melhor] - fim))) melhor = k;
  }
  return melhor >= 0 ? c.chance24[melhor] : NaN;
}

/**
 * Um resumo por dia local. A chuva de cada fatia de 3 h conta no dia do MEIO
 * dela (a fatia das 21–00 h é do dia que termina, não do seguinte).
 */
export function resumoPorDia(s: SeriesDoPonto, fuso: number, agora: number, diaTendencia = 7, cenarios: ChanceDe24h | null = null): ResumoDoDia[] {
  const dias = new Map<number, number[]>();
  for (let k = 0; k < s.t.length; k++) {
    const meio = s.t[k] - PASSO_GRADE_MS / 2;
    const d = inicioDoDiaLocal(meio, fuso);
    if (!dias.has(d)) dias.set(d, []);
    dias.get(d)!.push(k);
  }
  const hoje = inicioDoDiaLocal(agora, fuso);
  const out: ResumoDoDia[] = [];
  const ordenados = [...dias].sort((a, b) => a[0] - b[0]);
  for (const [n0, [inicio, ks]] of ordenados.entries()) {
    const n = Math.round((inicio - hoje) / (24 * HOUR_MS));
    // Antes de hoje: a primeira fatia pode ter o meio no dia anterior (entre 0 h e 1h30 no lugar).
    if (n < 0) continue;
    // O último dia da rodada com poucas horas (a de 12 UTC acaba de madrugada): fora — não é um dia.
    const ultimo = n0 === ordenados.length - 1;
    if (ultimo && ks.length < 4) continue;
    const temps = ks.map((k) => s.t2m[k]).filter(Number.isFinite);
    if (temps.length === 0) continue;
    const porParte = new Map<ParteDoDia, number>();
    let chuva = 0;
    for (const k of ks) {
      const mm = s.chuva[k] * 3;
      if (!Number.isFinite(mm)) continue;
      chuva += mm;
      const p = parteDoDia(horaLocal(s.t[k] - PASSO_GRADE_MS / 2, fuso));
      porParte.set(p, (porParte.get(p) ?? 0) + mm);
    }
    const pms = ks.map((k) => s.pm25[k]).filter(Number.isFinite);
    // Média de 24 h só com o dia coberto (8 fatias de 3 h; aceita 7).
    const mp25Media = pms.length >= 7 ? pms.reduce((a, b) => a + b, 0) / pms.length : NaN;
    const rajadas = ks.map((k) => s.rajada[k]).filter(Number.isFinite);
    out.push({
      inicio,
      rotulo: rotuloDia(inicio, fuso),
      rotuloLongo: rotuloDia(inicio, fuso, true),
      hoje: n === 0,
      parcial: n === 0 || ks.length < 8,
      tendencia: n >= diaTendencia,
      tMax: Math.max(...temps),
      tMin: Math.min(...temps),
      chuva,
      quandoChove: chuva >= 1 ? quando(porParte, chuva) : null,
      rajadaMax: rajadas.length ? Math.max(...rajadas) : NaN,
      mp25Media,
      ar: categoriaConama(mp25Media),
      chanceDia: chanceDoDia(cenarios, inicio),
    });
  }
  return out;
}
